<template>
  <v-autocomplete v-model="selectionValue" :search-input.sync="searchValue" :items="normalizedItems"
    item-value="ArticleNumber" item-text="ArticleNumber" :filter="() => true" :label="label" :placeholder="placeholder"
    hide-details hide-no-data :loading="loading || searching || eventualDataLoading" :clearable="clearable"
    ref="articleinput" dense append-icon="" name="article" autocomplete="off" @blur="stopTimer"
    @keypress.native="keypress" @change="onChange" @update:search-input="searchInput" @keydown.tab="keypress"
    @click:clear="onClear">

    <template v-slot:prepend-inner>
      <v-icon size="18" class="mt-1">mdi-magnify</v-icon>
    </template>

    <template v-slot:item="{ item, attrs, on }">
      <v-list-item v-on="on" v-bind="attrs" dense>
        <v-list-item-content>
          <v-list-item-title>{{ item.ArticleNumber }}</v-list-item-title>
          <v-list-item-subtitle>{{ item.ArticleDescription }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script>
import clientAPI from "@/api/ClientAPIMixin";
import EventualDataMixin from "@/helpers/Mixins/EventualDataMixin.js";
import SRConst from "@/helpers/Constants/SignalRConstants.js";

export default {
  name: "ArticleSearchMenuTextField",
  mixins: [EventualDataMixin],
  props: [
    "value",
    "label",
    "loading",
    "clearable",
    "placeholder",
  ],

  data() {
    return {
      searchValue: "",        // the text in the autocomplete input
      selectionValue: null,   // the selected ArticleNumber (string)
      searchTimeout: null,
      topSearchResponse: [],  // [{ ArticleNumber, ArticleDescription, ... }]
      awaitingSearch: false,
      searching: false,
      eventualDataLoading: false,
      empty_image: "",
    };
  },

  mounted() {
    this._boundEventualDataHandler = (args) => this.notifyChanges(args);
    this.$store.dispatch("NotificationStore/subscribe", [
      SRConst.EventKeys.EventualDataEvent,
      this._boundEventualDataHandler
    ]);

    this.setInternalValue(this.value);
  },

  beforeDestroy() {
    this.clearEventualDataTimeout();
    if (this._boundEventualDataHandler) {
      this.$store.dispatch("NotificationStore/unsubscribe", [
        SRConst.EventKeys.EventualDataEvent,
        this._boundEventualDataHandler
      ]);
    }
  },


  methods: {
    setInternalValue(v) {
      const str = typeof v === "string" ? v : "";
      this.selectionValue = str;
      this.searchValue = str || "";
    },

    notifyChanges(args) {
      if (!this.eventualDataLoading) {
        return;
      }
      if (this.mergeEventualDataIntoList(args, SRConst.EventualDataType.Article, this.topSearchResponse)) {
        this.eventualDataLoading = false;
      }
    },

    onClear() {
      // keep parent v-model and our internals in sync
      this.$emit("input", "");
      this.$emit("clear");
      this.selectionValue = "";
      this.searchValue = "";
    },

    onChange(articleNumber) {
      // Vuetify returns the item-value -> ArticleNumber (string)
      const number = typeof articleNumber === "string" ? articleNumber : "";

      if (!number) {
        this.onClear();
      }
      else {
        this.selectionValue = number;
        this.searchValue = number;
        this.$emit("input", number); // make v-model work
        // emit the full item if we have it; otherwise emit the string
        const found = this.topSearchResponse.find(x => x.ArticleNumber === number);
        this.$emit("selected", found || number); // for richer parent handling
        this.$emit("click", found || number);    // backward compat with old event name
      }
    },

    keypress({ key }) {
      if (key === "Enter" || key === "Tab") {
        this.selectItem(this.selectionValue || this.searchValue || "");
      }
      // Do not blank selection on other keys
    },

    stopTimer() {
      clearTimeout(this.searchTimeout);
      this.awaitingSearch = false;
    },

    searchInput(searchInputValue) {
      if (!searchInputValue || searchInputValue.length < 2) {
        this.topSearchResponse = [];
        return;
      }
      if (this.searchTimeout) clearTimeout(this.searchTimeout);

      const val = searchInputValue;
      if (val && val !== "") {
        this.topSearchResponse = [];
        this.awaitingSearch = true;
        this.searchTimeout = setTimeout(() => {
          this.awaitingSearch = false;
          this.searchTimeout = null;
          if (val.length <= 2) return;
          this.searchItemContext(val);
        }, 700);
      }
    },

    searchItemContext(searchParameter) {
      this.topSearchResponse = [];
      this.searching = true;
      this.eventualDataLoading = false;
      this.clearEventualDataTimeout();
      clientAPI.methods.searchArticle(searchParameter).then(data => {
        this.topSearchResponse = this.topSearchResponse.concat(data || []);
        this.searching = false;
        var eventualDataMode = this.$store.state.ERPConfig?.EventualDataMode;
        var connected = this.$store.getters['NotificationStore/connectionState'] === 'Connected';
        if (eventualDataMode && connected) {
          this.eventualDataLoading = true;
          this.scheduleEventualDataTimeout(() => {
            if (this.eventualDataLoading) this.eventualDataLoading = false;
          });
        }
      });
    },

    selectItem(item) {
      // item is a string ArticleNumber (or empty)
      let number = typeof item === "string" ? item : "";
      if (!number) number = this.searchValue || "";

      if (!number) {
        // nothing to select
        return;
      }

      // ensure there is at least one matching item for UI consistency
      if (this.topSearchResponse.length === 0) {
        this.topSearchResponse = [{ ArticleNumber: number, ArticleDescription: "" }];
      }

      this.onChange(number);
    },

    focus() {
      this.$refs.articleinput && this.$refs.articleinput.focus();
    },

  },

  computed: {
    normalizedItems() {
      const arr = this.topSearchResponse ? this.topSearchResponse.slice() : [];
      const current = (typeof this.selectionValue === 'string' && this.selectionValue !== '')
        ? this.selectionValue
        : (typeof this.value === 'string' ? this.value : '');
      if (current && !arr.some(i => i && i.ArticleNumber === current)) {
        arr.unshift({ ArticleNumber: current, ArticleDescription: '' });
      }
      return arr;
    }
  },

  watch: {
    // keep internal state in sync with external v-model
    value: {
      immediate: true,
      handler(v) {
        this.setInternalValue(v);
      }
    }
  },

};
</script>